import { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Menu.css";

const MENU_API = "https://salathai.co.tz/mrms/Menu.php";
const ITEMS_PER_PAGE = 5;

export default function Menu() {
    const [menuItems, setMenuItems] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [categoryFilter, setCategoryFilter] = useState("all");
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);

    const [formData, setFormData] = useState({
        group_button: "",
        item_name: "",
        price_tzs: ""
    });

    useEffect(() => { 
        loadMenu(); 
    }, []);

    const loadMenu = async () => {
        try {
            const res = await fetch(MENU_API);
            const data = await res.json();
            if (Array.isArray(data)) {
                const sorted = [...data].sort((a, b) => b.id - a.id);
                setMenuItems(sorted);
            } else {
                Swal.fire("Error", data.error || "Failed to load menu", "error");
            }
        } catch {
            Swal.fire("Error", "Server not reachable", "error");
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const isEdit = !!selectedItem;

        // Validate required fields
        if (!formData.group_button || !formData.item_name || !formData.price_tzs) {
            Swal.fire("Error", "Category, Item Name, and Price are required", "error");
            return;
        }

        setLoading(true);

        try {
            let res;
            let data;

            if (isEdit) {
                // UPDATE: Send only required fields
                const params = new URLSearchParams();
                params.append("group_button", formData.group_button);
                params.append("item_name", formData.item_name);
                params.append("price_tzs", formData.price_tzs);

                res = await fetch(`${MENU_API}?id=${selectedItem.id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: params.toString()
                });
                data = await res.json();

                if (!data.error) {
                    Swal.fire("Success", "Menu item updated successfully", "success");
                    setShowModal(false);
                    resetForm();
                    loadMenu();
                } else {
                    Swal.fire("Error", data.error, "error");
                }
            } else {
                // CREATE: Send form data
                const params = new URLSearchParams();
                params.append("group_button", formData.group_button);
                params.append("item_name", formData.item_name);
                params.append("price_tzs", formData.price_tzs);

                res = await fetch(MENU_API, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: params.toString()
                });
                data = await res.json();

                if (!data.error) {
                    Swal.fire("Success", "Menu item created successfully", "success");
                    setShowModal(false);
                    resetForm();
                    loadMenu();
                } else {
                    Swal.fire("Error", data.error, "error");
                }
            }
        } catch (error) {
            console.error("Request failed:", error);
            Swal.fire("Error", "Failed to connect to server", "error");
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            group_button: "",
            item_name: "",
            price_tzs: ""
        });
        setSelectedItem(null);
    };

    const editItem = (item) => {
        setSelectedItem(item);
        setFormData({
            group_button: item.group_button,
            item_name: item.item_name,
            price_tzs: item.price_tzs
        });
        setShowModal(true);
    };

    const deleteItem = async (item) => {
        const confirm = await Swal.fire({
            title: "Delete menu item?",
            text: item.item_name,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!"
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await fetch(`${MENU_API}?id=${item.id}`, { method: "DELETE" });
            const data = await res.json();
            
            if (data.message) {
                Swal.fire("Deleted!", data.message, "success");
                loadMenu();
            } else if (data.error) {
                Swal.fire("Error", data.error, "error");
            }
        } catch {
            Swal.fire("Error", "Failed to delete item", "error");
        }
    };

    const categories = useMemo(() => {
        const groups = [...new Set(menuItems.map(i => i.group_button))];
        return ["all", ...groups];
    }, [menuItems]);

    const filteredItems = useMemo(() => {
        return menuItems.filter(item => {
            const matchSearch = item.item_name.toLowerCase().includes(searchTerm.toLowerCase());
            const matchCategory = categoryFilter === "all" || item.group_button === categoryFilter;
            return matchSearch && matchCategory;
        });
    }, [menuItems, searchTerm, categoryFilter]);

    const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
    const paginatedItems = filteredItems.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    return (
        <div className="pm-menu-container">
            <div className="pm-menu-header">
                <h2 className="pm-menu-title">🍽 Menu Management</h2>
                <button 
                    className="pm-btn-primary" 
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                >
                    + Add Item
                </button>
            </div>

            <div className="pm-menu-controls">
                <input
                    type="text"
                    placeholder="🔍 Search menu..."
                    className="pm-search-input"
                    value={searchTerm}
                    onChange={(e) => { 
                        setSearchTerm(e.target.value); 
                        setCurrentPage(1); 
                    }}
                />
                <select
                    className="pm-filter-select"
                    value={categoryFilter}
                    onChange={(e) => { 
                        setCategoryFilter(e.target.value); 
                        setCurrentPage(1); 
                    }}
                >
                    {categories.map(cat => (
                        <option key={cat} value={cat}>
                            {cat === "all" ? "All Categories" : cat}
                        </option>
                    ))}
                </select>
            </div>

            <div className="pm-table-wrapper">
                <table className="pm-menu-table">
                    <thead>
                        <tr>
                            <th>Category</th>
                            <th>Item Name</th>
                            <th>Price (TZS)</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedItems.length > 0 ? (
                            paginatedItems.map(item => (
                                <tr key={item.id}>
                                    <td><span className="pm-badge">{item.group_button}</span></td>
                                    <td>{item.item_name}</td>
                                    <td>TZS {Number(item.price_tzs).toLocaleString()}</td>
                                    <td className="pm-action-buttons">
                                        <button 
                                            onClick={() => editItem(item)} 
                                            className="pm-btn-edit" 
                                            title="Edit"
                                        >
                                            ✏️ Edit
                                        </button>
                                        <button 
                                            onClick={() => deleteItem(item)} 
                                            className="pm-btn-delete" 
                                            title="Delete"
                                        >
                                            🗑️ Delete
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{ textAlign: "center", padding: "40px" }}>
                                    No menu items found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="pm-pagination">
                    <button 
                        disabled={currentPage === 1} 
                        onClick={() => setCurrentPage(p => p - 1)}
                    >
                        ← Prev
                    </button>
                    <span>Page {currentPage} of {totalPages}</span>
                    <button 
                        disabled={currentPage === totalPages} 
                        onClick={() => setCurrentPage(p => p + 1)}
                    >
                        Next →
                    </button>
                </div>
            )}

            {showModal && (
                <div className="pm-modal-overlay" onClick={() => {
                    if (!loading) setShowModal(false);
                }}>
                    <div className="pm-modal-box" onClick={e => e.stopPropagation()}>
                        <h3>{selectedItem ? "Edit Menu Item" : "Add New Menu Item"}</h3>
                        <form onSubmit={handleSubmit} className="pm-form">
                            <input
                                type="text"
                                name="group_button"
                                placeholder="Category *"
                                value={formData.group_button}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                autoFocus
                            />
                            
                            <input
                                type="text"
                                name="item_name"
                                placeholder="Item Name *"
                                value={formData.item_name}
                                onChange={handleChange}
                                required
                                disabled={loading}
                            />
                            
                            <input
                                type="number"
                                name="price_tzs"
                                placeholder="Price (TZS) *"
                                value={formData.price_tzs}
                                onChange={handleChange}
                                required
                                disabled={loading}
                                step="100"
                                min="0"
                            />
                            
                            <div className="pm-form-actions">
                                <button 
                                    type="submit" 
                                    className="pm-btn-primary"
                                    disabled={loading}
                                >
                                    {loading ? "Processing..." : (selectedItem ? "Update" : "Create")}
                                </button>
                                <button 
                                    type="button" 
                                    className="pm-btn-secondary" 
                                    onClick={() => setShowModal(false)}
                                    disabled={loading}
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}